import { $http } from '@/common/api/http.service';
import { useAuthStore } from '@/stores/auth';

/**
 * Telehealth API — the host (account-scoped, authenticated) endpoints and the
 * participant join (public, no-login) endpoints served by the Symfony backend.
 *
 * Every call resolves to the backend envelope `{ success, data, error }`.
 */

function accountBase() {
    const authStore = useAuthStore();
    return `/api/account/${authStore.account.id}/telehealth`;
}

// ---------------------------------------------------------------------------
// Host (authenticated, account-scoped)
// ---------------------------------------------------------------------------

export async function getMeeting(meetingId) {
    const response = await $http.get(`${accountBase()}/meetings/${meetingId}`);
    return response.data;
}

/** Resolve a meeting (host view) by its appointment — the host window's entry point. */
export async function getMeetingByAppointment(appointmentId) {
    const response = await $http.get(`${accountBase()}/by-appointment/${appointmentId}`, { errorHandle: false });
    return response.data;
}

/**
 * Reconcile an appointment's telehealth state after it saves: creates the meeting
 * + per-participant links when telehealth is on, deletes it when off, else no-op.
 *
 * Only the appointment id(s) and the checkbox are sent — the server reads the roster
 * and each person's date of birth from the appointment and their charts, so the
 * browser cannot nominate a host, add an attendee, or choose the join gate.
 *
 * @param {object} payload { appointmentIds: string[], telehealth: boolean, mode?: string }
 */
export async function reconcileTelehealth(payload) {
    const response = await $http.post(`${accountBase()}/reconcile`, payload);
    return response.data;
}

/**
 * The practitioner ids in this account eligible to host telehealth (their user
 * holds the Host-telehealth-sessions permission). Used to gate the Telehealth
 * checkbox on the selected rendering provider.
 */
export async function getHostingPractitioners() {
    const response = await $http.get(`${accountBase()}/hosting-practitioners`);
    return response.data;
}

/** Start/Join — provisions the BNT room. Server-side enforces host permission + identity. */
export async function startMeeting(meetingId) {
    const response = await $http.post(`${accountBase()}/meetings/${meetingId}/start`);
    return response.data;
}

/** Admit one participant, or all waiting participants when participantId is omitted. */
export async function admitParticipant(meetingId, participantId = 'all') {
    const response = await $http.post(`${accountBase()}/meetings/${meetingId}/admit`, { participantId });
    return response.data;
}

/**
 * Start (or rejoin) an ad-hoc session for a standalone form.
 *
 * Keyed on the form's QuestionnaireResponse: a standalone form has no
 * appointment and no encounter either. The patient is added but left in the
 * waiting room; nothing is sent to them.
 */
export async function startAdhocSession(questionnaireResponseId, patientId) {
    const response = await $http.post(`${accountBase()}/adhoc`, { questionnaireResponseId, patientId }, { errorHandle: false });
    return response.data;
}

/** Send an admitted participant back to the waiting room; admit() reverses it. */
export async function holdParticipant(meetingId, participantId) {
    const response = await $http.post(`${accountBase()}/meetings/${meetingId}/hold`, { participantId }, { errorHandle: false });
    return response.data;
}

export async function endMeeting(meetingId) {
    const response = await $http.post(`${accountBase()}/meetings/${meetingId}/end`);
    return response.data;
}

/**
 * Add one contact to a live meeting (#5015 AC13). Only the FHIR reference is sent —
 * the server resolves the contact's date-of-birth gate and display name from the
 * chart and mints their own join token.
 *
 * @param {string} meetingId
 * @param {object} contact { resourceType, id }
 */
export async function addParticipant(meetingId, contact) {
    const response = await $http.post(`${accountBase()}/meetings/${meetingId}/participants`, contact);
    return response.data;
}

/**
 * Send a participant their join link over one channel (#5015 AC12). A channel with
 * nothing on file resolves to `{ success: false, error }` (422) so the caller can
 * surface why.
 *
 * @param {'sms'|'email'} channel
 */
export async function inviteParticipant(meetingId, participantId, channel) {
    const response = await $http.post(`${accountBase()}/meetings/${meetingId}/participants/${participantId}/invite`, { channel }, { errorHandle: false });
    return response.data;
}

/** Per-participant invite availability ({ id, sms, email }); fetched on host-window open. */
export async function getInviteAvailability(meetingId) {
    const response = await $http.get(`${accountBase()}/meetings/${meetingId}/invite-availability`);
    return response.data;
}

// --- In-session content sharing (#5040) ------------------------------------

/**
 * Share one resource into the session. Replaces whatever was shared, and always
 * starts read-only for participants — handing over the pen is a separate call.
 *
 * @param {'form'|'whiteboard'|'link'} kind
 * @param {?string} ref QuestionnaireResponse id for a form; null otherwise
 */
export async function shareContent(meetingId, kind, ref, title) {
    const response = await $http.post(`${accountBase()}/meetings/${meetingId}/share`, { kind, ref, title }, { errorHandle: false });
    return response.data;
}

export async function unshareContent(meetingId) {
    const response = await $http.post(`${accountBase()}/meetings/${meetingId}/unshare`);
    return response.data;
}

/**
 * Hand the pen to one named participant, or take it back.
 *
 * @param {'host'|'participant'} pen
 * @param {?string} participantId required when handing it over — a session can
 *   hold several participants, so the recipient is named rather than implied.
 */
export async function setPen(meetingId, pen, participantId = null) {
    const response = await $http.post(`${accountBase()}/meetings/${meetingId}/pen`, { pen, participantId }, { errorHandle: false });
    return response.data;
}

/**
 * The shared form exactly as the participant sees it, for the clinician's
 * "view as patient" check. Read-only — the clinician edits in the real form
 * filler, which writes to the chart directly.
 */
/**
 * Tell the participant the host just changed the shared form.
 *
 * Fire-and-forget by design: it carries no answers, and a lost nudge only means
 * the patient sees the edit on the clinician's next keystroke instead of this
 * one. Never blocks the clinician's own save.
 */
export async function noteHostFormEdit(meetingId, questionnaireResponseId) {
    const response = await $http.post(`${accountBase()}/meetings/${meetingId}/shared-form/touched`, { questionnaireResponseId }, { errorHandle: false });
    return response.data;
}

export async function getHostSharedForm(meetingId) {
    const response = await $http.get(`${accountBase()}/meetings/${meetingId}/shared-form`, { errorHandle: false });
    return response.data;
}

// ---------------------------------------------------------------------------
// Participant join (public, no login)
// ---------------------------------------------------------------------------

function publicBase(joinToken) {
    return `/api/telehealth-public/join/${joinToken}`;
}

// Public calls handle their own errors on the join page (wrong DOB = 401,
// dead link = 410), so suppress the global error toast (errorHandle: false)
// while still rejecting so the component can react.
const publicOpts = (verifyToken) => ({
    noAuth: true,
    errorHandle: false,
    ...(verifyToken ? { headers: { 'X-Telehealth-Verify': verifyToken } } : {}),
});

/** Submit date of birth to verify. On success returns state + a verify token. */
export async function verifyJoin(joinToken, dob) {
    const response = await $http.post(`${publicBase(joinToken)}/verify`, { dob }, publicOpts());
    return response.data;
}

/** Poll the participant's current state (post-verify). */
export async function getJoinState(joinToken, verifyToken) {
    const response = await $http.get(`${publicBase(joinToken)}/state`, publicOpts(verifyToken));
    return response.data;
}

/** Presence heartbeat / arrival ping (post-verify). */
export async function joinHeartbeat(joinToken, verifyToken) {
    const response = await $http.post(`${publicBase(joinToken)}/heartbeat`, {}, publicOpts(verifyToken));
    return response.data;
}

/**
 * The form the host has shared into the session, with the Questionnaire that
 * defines it (#5040). The participant never names a resource — the server
 * resolves it from meeting state — so there is nothing here to probe.
 */
export async function getSharedForm(joinToken, verifyToken) {
    const response = await $http.get(`${publicBase(joinToken)}/shared-form`, publicOpts(verifyToken));
    return response.data;
}

/**
 * Save one answer into the shared form. Refused (409) unless the participant
 * currently holds the pen.
 *
 * @param {Array} answer FHIR answer array as built by the form renderer
 */
export async function saveSharedAnswer(joinToken, verifyToken, linkId, answer) {
    const response = await $http.post(`${publicBase(joinToken)}/shared-form/answer`, { linkId, answer }, publicOpts(verifyToken));
    return response.data;
}
